/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    Configuration,
    ConfigurationParameters,
    PreviewTagApi,
    ScreenType,
    OfferPreviewGetRequest,
    VodOfferPreviewGetRequest,
} from 'client-code/previews';
import { Constants } from '../constants';
import { IPreviewsServiceRepository } from '../interfaces/IPreviewsServiceRepository';
import { TServicesParams, TOfferPreview } from '../types';
import { Log } from '../logging/Log';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { CommonHeaders } from '../CommonHeaders';
import { DeviceModel } from '../device/DeviceModel';
import { OfferPreview } from '../entities';

export class PreviewsRepositoryImpl implements IPreviewsServiceRepository {
    mParams: TServicesParams;

    TAG: string = 'PreviewsRepositoryImpl';

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private getConfigurationParameters(commonHeaders): ConfigurationParameters {
        return {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };
    }

    private getRequestParameters(screenType: ScreenType, commonHeaders) {
        return {
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            applicationName: DeviceModel.getTiVoSettings().getAppName(),
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationVersion: commonHeaders.ApplicationVersion,
            bodyId: this.mParams.bodyId,
            deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
            productName: DeviceModel.getTiVoSettings().getAppName(),
            acceptLanguage: DeviceModel.getTVProperties().getDateLocale(),
            screenType,
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
        };
    }

    private async getOfferPreviewFromService(
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        try {
            const commonHeaders = new CommonHeaders(screenType).headers;
            const offerPreviewConfigParameters = this.getConfigurationParameters(commonHeaders);
            const offerPreviewRequestParameters: OfferPreviewGetRequest = {
                offerId,
                ...this.getRequestParameters(screenType, commonHeaders),
            };

            const configuration = new Configuration(offerPreviewConfigParameters);
            const previewsOfferApi = new PreviewTagApi(configuration);
            // screenType is marked as optional in schema but server needs it
            const previewsOfferResponse = await previewsOfferApi.offerPreviewGet(
                offerPreviewRequestParameters,
            );

            const offerPreviewResponse = new OfferPreview({
                offerPreview: previewsOfferResponse.data || {},
                imageHostBaseUrl: this.mParams.imageHostBaseUrl,
                networkCallId,
            });
            return offerPreviewResponse;
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(this.TAG, 'getOfferPreviewFromService()', error);
            throw error;
        }
    }

    private async getVodOfferPreviewFromService(
        vodOfferId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        try {
            const commonHeaders = new CommonHeaders(screenType).headers;
            const vodOfferPreviewConfigParameters = this.getConfigurationParameters(commonHeaders);
            const vodOfferPreviewRequestParameters: VodOfferPreviewGetRequest = {
                vodOfferId,
                ...this.getRequestParameters(screenType, commonHeaders),
            };

            const configuration = new Configuration(vodOfferPreviewConfigParameters);
            const previewsVodOfferApi = new PreviewTagApi(configuration);
            // screenType is marked as optional in schema but server needs it
            const previewsVodOfferResponse = await previewsVodOfferApi.vodOfferPreviewGet(
                vodOfferPreviewRequestParameters,
            );
            const vodOfferPreviewResponse = new OfferPreview({
                offerPreview: previewsVodOfferResponse.data || {},
                networkCallId,
            });
            return vodOfferPreviewResponse;
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(this.TAG, 'getVodOfferPreviewFromService()', error);
            throw error;
        }
    }

    /**
     * function to get offer preview for particular program
     * @param offerId {string}
     * @param screenType {ScreenType}
     * @param networkCallId {number}
     * @returns OfferPreview
     */
    async fetchOfferPreview(
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        try {
            return await this.getOfferPreviewFromService(offerId, screenType, networkCallId);
        } catch (error) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * function to get vod offer preview for particular program
     * @param vodOfferId {string}
     * @param screenType {ScreenType}
     * @param networkCallId {number}
     * @returns OfferPreview
     */
    async fetchVodOfferPreview(
        vodOfferId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        try {
            return await this.getVodOfferPreviewFromService(vodOfferId, screenType, networkCallId);
        } catch (error) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }
}
